<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Request Fund</h1></div>
        </div>
      </div>

      <!-- REQUEST FORM -->
      <div class="row justify-content-center">
        <div class="col-lg-8 col-md-10">
          <div class="req-card">
            <h5 class="card-title">Request money from an Amejpay user</h5>
            <b-form @submit.stop.prevent="verifyRecipient">
              <div class="row">
                <div class="col-12 col-md-6 form-row-x">
                  <label>Recipient Email *</label>
                  <b-form-input v-model.trim="form.to" type="email" placeholder="name@example.com"></b-form-input>
                  <span class="text-danger" v-if="errors.to">{{ errors.to }}</span>
                </div>
                <div class="col-12 col-md-6 form-row-x">
                  <label>Amount (USD) *</label>
                  <b-form-input
                    v-model="form.amount" placeholder="0.00"
                    oninput="this.value = this.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');"
                  ></b-form-input>
                  <span class="text-danger" v-if="errors.amount">{{ errors.amount }}</span>
                </div>
              </div>
              <div class="form-row-x">
                <label>Remarks</label>
                <b-form-input v-model.trim="form.remarks" placeholder="What is this request for?"></b-form-input>
              </div>
              <div class="text-danger mb-2" v-if="formError">{{ formError }}</div>
              <b-button variant="primary" type="submit" :disabled="isChecking">
                <b-spinner small v-if="isChecking" class="mr-2"></b-spinner>
                {{ isChecking ? "Checking..." : "Continue" }}
              </b-button>
            </b-form>
          </div>
        </div>
      </div>

      <!-- DIRECTION TABS -->
      <div class="row mt-4">
        <div class="col-12">
          <div class="tabs-row">
            <button
              type="button" class="tab-btn"
              :class="activeTab === 'incoming' ? 'is-active' : ''"
              @click="activeTab = 'incoming'"
            >
              Incoming
              <span class="badge-count" v-if="pendingIncoming">{{ pendingIncoming }}</span>
            </button>
            <button
              type="button" class="tab-btn"
              :class="activeTab === 'outgoing' ? 'is-active' : ''"
              @click="activeTab = 'outgoing'"
            >Outgoing</button>
          </div>
          <p class="muted mt-2">
            <template v-if="activeTab === 'incoming'">
              Requests other users sent to you. You can accept or reject them.
            </template>
            <template v-else>
              Requests you sent. No action is needed from your side.
            </template>
          </p>
        </div>
      </div>

      <!-- LIST -->
      <div class="row">
        <div class="col-12">
          <b-table
            hover responsive striped bordered outlined show-empty
            :items="visibleRequests" :fields="fields" :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center py-4"><b-spinner label="Loading..."></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center py-3">No {{ activeTab }} requests.</div>
            </template>
            <template #cell(index)="data">{{ data.index + 1 }}.</template>
            <template #cell(party)="data">
              <div class="party">
                <img class="avatar-sm" v-if="partyOf(data.item).image" :src="partyOf(data.item).image" />
                <div>
                  <div>{{ partyOf(data.item).full_name || '-' }}</div>
                  <small class="muted">{{ partyOf(data.item).email || '' }}</small>
                </div>
              </div>
            </template>
            <template #cell(amount)="data">$ {{ money(data.item.amount) }}</template>
            <template #cell(status)="data">
              <span class="status-pill" :class="statusClass(data.item.status)">
                {{ prettify(data.item.status) }}
              </span>
            </template>
            <template #cell(date)="data">{{ formatDate(data.item.date) }}</template>
            <template #cell(actions)="data">
              <template v-if="canRespond(data.item)">
                <a href="#" class="btn btn-success btn-sm mr-1" @click.prevent="askAccept(data.item)">Accept</a>
                <a href="#" class="btn btn-danger btn-sm" @click.prevent="askReject(data.item)">Reject</a>
              </template>
              <span class="muted" v-else>&mdash;</span>
            </template>
          </b-table>
        </div>
      </div>
    </div>

    <!-- CONFIRM SENDING A REQUEST -->
    <b-modal id="req-confirm-modal" centered title="Confirm Request" :no-close-on-backdrop="true">
      <div class="recipient-box" v-if="recipient && recipient.email">
        <img class="avatar" :src="recipient.image" v-if="recipient.image" />
        <div>
          <div class="rname">{{ recipient.full_name }}</div>
          <div class="remail">{{ recipient.email }}</div>
        </div>
      </div>
      <div class="detail-row"><span>Amount</span><strong>$ {{ money(form.amount) }}</strong></div>
      <div class="detail-row"><span>Remarks</span><strong>{{ form.remarks || '-' }}</strong></div>
      <p class="note mt-2">This user will be asked to approve your request.</p>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSending" @click="sendRequest">
          <b-spinner small v-if="isSending" class="mr-1"></b-spinner>
          {{ isSending ? "Sending..." : "Send Request" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Edit</b-button>
      </template>
    </b-modal>

    <!-- REJECT -->
    <b-modal id="req-reject-modal" centered title="Reject Request">
      <p v-if="target">
        Reject the request for <strong>$ {{ money(target.amount) }}</strong>
        from <strong>{{ partyOf(target).full_name }}</strong>?
      </p>
      <p class="note">This cannot be undone.</p>
      <div class="text-danger" v-if="respondError">{{ respondError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="danger" :disabled="isResponding" @click="doReject">
          <b-spinner small v-if="isResponding" class="mr-1"></b-spinner>
          {{ isResponding ? "Rejecting..." : "Yes, Reject" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Keep</b-button>
      </template>
    </b-modal>

    <!-- ACCEPT : OTP + PASSWORD -->
    <b-modal id="req-otp-modal" centered title="Approve & Pay" :no-close-on-backdrop="true">
      <p class="note" v-if="target">
        Enter the code sent to your email and your account password to pay
        <strong>$ {{ money(target.amount) }}</strong> to
        <strong>{{ partyOf(target).full_name }}</strong>.
      </p>
      <div class="form-row-x">
        <label>Verification Code *</label>
        <b-form-input
          v-model.trim="otp" maxlength="6" placeholder="6-digit code"
          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
        ></b-form-input>
        <span class="text-danger" v-if="errors.otp">{{ errors.otp }}</span>
      </div>
      <div class="form-row-x">
        <label>Account Password *</label>
        <b-form-input v-model="password" type="password" placeholder="Your login password"></b-form-input>
        <span class="text-danger" v-if="errors.password">{{ errors.password }}</span>
      </div>
      <div class="text-danger" v-if="respondError">{{ respondError }}</div>
      <div class="mt-2">
        <a href="#" class="link-muted" v-if="resendCooldown === 0" @click.prevent="resendAcceptOtp">Resend code</a>
        <span class="muted" v-else>Resend code in {{ resendCooldown }}s</span>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isConfirming" @click="doConfirmAccept">
          <b-spinner small v-if="isConfirming" class="mr-1"></b-spinner>
          {{ isConfirming ? "Processing..." : "Approve & Pay" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <b-toast id="req-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
    <b-toast id="req-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Request Failed</strong>
      </div>
      <div><p>{{ respondError }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "RequestFund",
  components: { "app-header": AppHeader },
  data() {
    return {
      activeTab: "incoming",
      isLoading: false,
      isChecking: false,
      isSending: false,
      isResponding: false,
      isConfirming: false,
      form: { to: "", amount: "", remarks: "" },
      errors: {},
      formError: "",
      respondError: "",
      infoText: "",
      target: null,
      otp: "",
      password: "",
      resendCooldown: 0,
      cooldownTimer: null,
      fields: [
        { key: "index", label: "S.N." },
        { key: "party", label: "User" },
        { key: "amount", label: "Amount" },
        { key: "remarks", label: "Remarks" },
        { key: "status", label: "Status" },
        { key: "date", label: "Date" },
        { key: "actions", label: "Action" },
      ],
      valid_email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
    };
  },
  computed: {
    ...mapState(["paymentRequests", "recipient", "profile"]),
    requests: function () {
      return Array.isArray(this.paymentRequests) ? this.paymentRequests : [];
    },
    visibleRequests: function () {
      const tab = this.activeTab;
      return this.requests.filter(function (r) { return r && r.direction === tab; });
    },
    pendingIncoming: function () {
      const vm = this;
      return this.requests.filter(function (r) {
        return r && r.direction === "incoming" && vm.isPending(r.status);
      }).length;
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopCooldown();
  },
  methods: {
    ...mapActions([
      "listPaymentRequests", "createPaymentRequest",
      "respondPaymentRequest", "confirmPaymentRequest", "lookupRecipient",
    ]),

    load() {
      this.isLoading = true;
      const vm = this;
      return this.listPaymentRequests()
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },

    /**
     * `outgoing` rows are ones the signed-in user created, so the other party is the
     * recipient. `incoming` rows were created by someone else, so the other party is
     * the author. (The old page picked columns from requests[0] for the whole table.)
     */
    partyOf(r) {
      if (!r) { return {}; }
      return r.direction === "outgoing" ? (r.recipient || {}) : (r.author || {});
    },
    isPending(status) {
      const s = String(status || "").toLowerCase();
      return s === "initiated" || s === "pending" || s === "requested";
    },
    /** Only an incoming request that is still pending can be accepted or rejected. */
    canRespond(r) {
      return !!r && r.direction === "incoming" && this.isPending(r.status);
    },
    statusClass(status) {
      const s = String(status || "").toLowerCase();
      if (s === "accepted" || s === "completed" || s === "success") { return "is-success"; }
      if (this.isPending(s)) { return "is-pending"; }
      return "is-failed";
    },
    prettify(v) {
      if (!v) { return "-"; }
      return String(v).split("_").map(function (w) {
        return w.charAt(0).toUpperCase() + w.slice(1);
      }).join(" ");
    },
    money(v) { const n = Number(v); return (isNaN(n) ? 0 : n).toFixed(2); },
    formatDate(value) {
      if (!value) { return ""; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },

    // ---------- create ----------
    validateForm() {
      const e = {};
      if (!this.form.to) { e.to = "Recipient email is required."; }
      else if (!this.valid_email.test(this.form.to)) { e.to = "Enter a valid email address."; }
      const amt = Number(this.form.amount);
      if (!String(this.form.amount).trim()) { e.amount = "Amount is required."; }
      else if (isNaN(amt) || amt <= 0) { e.amount = "Amount must be greater than 0."; }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    verifyRecipient() {
      this.formError = "";
      if (!this.validateForm()) { return; }
      this.isChecking = true;
      const vm = this;
      return this.lookupRecipient(this.form.to)
        .then(function (res) {
          if (!res || !res.ok) {
            vm.formError = (res && res.message) || "Could not verify this user.";
            return;
          }
          if (res.recipient && res.recipient.is_self) {
            vm.formError = "You cannot request money from your own account.";
            return;
          }
          vm.$bvModal.show("req-confirm-modal");
        })
        .then(function () { vm.isChecking = false; })
        .catch(function () { vm.isChecking = false; });
    },
    sendRequest() {
      this.isSending = true;
      const vm = this;
      return this.createPaymentRequest({
        amount: Number(this.form.amount),
        to: this.form.to,
        remarks: this.form.remarks || "",
        date: new Date().toISOString(),
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "Your payment request has been sent.";
          vm.$bvModal.hide("req-confirm-modal");
          vm.$bvToast.show("req-toast");
          vm.form = { to: "", amount: "", remarks: "" };
          vm.errors = {};
          vm.activeTab = "outgoing";
          return vm.load();
        }
        vm.respondError = (res && res.message) || "Unable to send this request.";
        vm.$bvModal.hide("req-confirm-modal");
        vm.$bvToast.show("req-error-toast");
      }).then(function () { vm.isSending = false; })
        .catch(function () { vm.isSending = false; });
    },

    // ---------- respond ----------
    askReject(item) {
      this.target = item;
      this.respondError = "";
      this.$bvModal.show("req-reject-modal");
    },
    doReject() {
      if (!this.target) { return; }
      this.isResponding = true;
      const vm = this;
      return this.respondPaymentRequest({
        transaction_id: this.target.id,
        date: new Date().toISOString(),
        status: "rejected",
      }).then(function (res) {
        if (res && res.ok) {
          // A rejection completes immediately - no OTP step.
          vm.infoText = res.message || "Request rejected.";
          vm.$bvModal.hide("req-reject-modal");
          vm.$bvToast.show("req-toast");
          return vm.load();
        }
        vm.respondError = (res && res.message) || "Unable to reject this request.";
      }).then(function () { vm.isResponding = false; })
        .catch(function () { vm.isResponding = false; });
    },
    askAccept(item) {
      this.target = item;
      this.otp = "";
      this.password = "";
      this.errors = {};
      this.respondError = "";
      this.isResponding = true;
      const vm = this;
      // Accepting first asks the server to send an OTP.
      return this.respondPaymentRequest({
        transaction_id: item.id,
        date: new Date().toISOString(),
        status: "accepted",
      }).then(function (res) {
        if (res && res.ok) {
          vm.$bvModal.show("req-otp-modal");
          vm.startCooldown();
          return;
        }
        vm.respondError = (res && res.insufficientBalance)
          ? "You do not have enough balance to accept this request."
          : ((res && res.message) || "Unable to accept this request.");
        vm.$bvToast.show("req-error-toast");
      }).then(function () { vm.isResponding = false; })
        .catch(function () { vm.isResponding = false; });
    },
    resendAcceptOtp() {
      if (this.resendCooldown > 0 || !this.target) { return; }
      const vm = this;
      this.respondError = "";
      return this.respondPaymentRequest({
        transaction_id: this.target.id,
        date: new Date().toISOString(),
        status: "accepted",
      }).then(function (res) {
        if (res && res.ok) {
          vm.otp = "";
          vm.infoText = "A new verification code has been sent to your email.";
          vm.$bvToast.show("req-toast");
        } else {
          vm.respondError = (res && res.message) || "Unable to resend the code.";
        }
        vm.startCooldown();
      });
    },
    doConfirmAccept() {
      const e = {};
      if (!this.otp) { e.otp = "Verification code is required."; }
      else if (this.otp.length !== 6) { e.otp = "Enter the 6-digit code."; }
      if (!this.password) { e.password = "Account password is required."; }
      this.errors = e;
      this.respondError = "";
      if (Object.keys(e).length) { return; }

      this.isConfirming = true;
      const vm = this;
      return this.confirmPaymentRequest({
        transaction_id: this.target.id,
        otp: this.otp,
        password: this.password,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "You have accepted the request.";
          vm.password = "";
          vm.stopCooldown();
          vm.$bvModal.hide("req-otp-modal");
          vm.$bvToast.show("req-toast");
          return vm.load();
        }
        if (res && res.insufficientBalance) {
          vm.respondError = "You do not have enough balance to accept this request.";
        } else if (res && res.passwordError) {
          vm.respondError = res.message || "That password is not correct.";
          vm.$set(vm.errors, "password", vm.respondError);
        } else if (res && res.otpError) {
          vm.respondError = res.message || "That code is invalid or has expired. You can resend it.";
          vm.$set(vm.errors, "otp", vm.respondError);
        } else {
          vm.respondError = (res && res.message) || "Unable to confirm this request.";
        }
      }).then(function () { vm.isConfirming = false; })
        .catch(function () {
          vm.isConfirming = false;
          vm.respondError = "Unable to confirm this request. Please try again.";
        });
    },

    startCooldown() {
      this.stopCooldown();
      this.resendCooldown = 60;
      const vm = this;
      this.cooldownTimer = setInterval(function () {
        vm.resendCooldown = vm.resendCooldown - 1;
        if (vm.resendCooldown <= 0) { vm.stopCooldown(); }
      }, 1000);
    },
    stopCooldown() {
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
      this.resendCooldown = 0;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../css/style.scss';

.req-card {
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 10px;
  padding: 26px; margin-bottom: 10px;
}
.card-title { font-size: 16px; font-weight: 600; margin-bottom: 16px; }
.form-row-x { margin-bottom: 16px; }
.form-row-x label { display: block; font-size: 14px; color: #6c757d; margin-bottom: 6px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.muted { color: #6c757d; font-size: 13px; }
.note { font-size: 13px; color: #6c757d; }
.link-muted { color: #0066b3; font-weight: 600; font-size: 14px; }
.tabs-row { display: inline-flex; border: 1px solid #e3e7eb; border-radius: 22px; overflow: hidden; }
.tab-btn {
  border: none; background: #fff; color: #6c757d; font-size: 14px; font-weight: 600;
  padding: 8px 22px; cursor: pointer; outline: none;
}
.tab-btn.is-active { background: #0066b3; color: #fff; }
.badge-count {
  display: inline-block; background: #dc3545; color: #fff; font-size: 11px;
  border-radius: 10px; padding: 0 7px; margin-left: 6px;
}
.tab-btn.is-active .badge-count { background: #fff; color: #0066b3; }
.party { display: flex; align-items: center; gap: 8px; }
.avatar-sm { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.avatar { width: 46px; height: 46px; border-radius: 50%; object-fit: cover; }
.recipient-box {
  display: flex; align-items: center; gap: 12px;
  background: #f6f8fa; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;
}
.rname { font-weight: 700; }
.remail { font-size: 13px; color: #6c757d; }
.detail-row {
  display: flex; justify-content: space-between; padding: 8px 2px;
  border-bottom: 1px solid #f2f4f6; font-size: 14px; gap: 16px;
}
.detail-row:last-child { border-bottom: none; }
.detail-row span { color: #6c757d; }
.status-pill { font-size: 11px; padding: 2px 9px; border-radius: 10px; font-weight: 600; white-space: nowrap; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-pending { background: #fdf3e0; color: #e08b00; }
.status-pill.is-failed { background: #fde3e5; color: #dc3545; }
</style>
